/**
 * Smart Shikka AI Core — PR99
 * Safe Internal Tool Execution Registry — Deterministic Internal Read-Only Tools Only
 */
function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
const BLOCKED_ACTIONS = [
    "browser_action",
    "child_process",
    "desktop_action",
    "file_write",
    "network_call",
    "old_assistant_alias",
    "secret_literal",
    "shell_command",
    "unapproved_db_write",
    "unapproved_file_write",
    "unapproved_network_call",
    "unknown_provider_call",
    "unknown_tool_execution",
    "unredacted_secret",
    "unsafe_env_dump",
    "user_drive_access",
    "agent_execution",
    "role_mutation",
    "permission_mutation",
    "runtime_unlock",
    "workflow_execution",
    "file_read",
    "gate_unlock",
    "broad_runtime_unlock",
    "unscoped_approval",
    "approval_without_audit",
    "expired_approval_use",
    "tool_execution_without_queue",
    "agent_execution_without_queue",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr99: false,
    secretLiteralAllowedInPr99: false,
    secretReadAllowedInPr99: false,
    runtimeUnlockAllowedInPr99: false,
    gateUnlockAllowedInPr99: false,
    agentExecutionAllowedInPr99: false,
    toolExecutionAllowedInPr99: false,
    workflowExecutionAllowedInPr99: false,
    documentRenderAllowedInPr99: false,
    iconInjectionAllowedInPr99: false,
    browserAutomationAllowedInPr99: false,
    desktopAutomationAllowedInPr99: false,
    fileMutationAllowedInPr99: false,
    fileReadAllowedInPr99: false,
    dbWriteAllowedInPr99: false,
    shellCommandAllowedInPr99: false,
    childProcessAllowedInPr99: false,
    roleMutationAllowedInPr99: false,
    permissionMutationAllowedInPr99: false,
    broadRuntimeUnlockAllowedInPr99: false,
    unscopedApprovalAllowedInPr99: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "safe_tool_execution_summary",
        displayName: "Safe Tool Execution Summary",
        description: "Summary of first safe tool execution scope. Only deterministic internal read-only tools from explicit allowlist.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Only deterministic internal read-only tools allowed", "No shell, browser, desktop, network, provider, file mutation, or drive access", "Explicit allowlist required"],
        futurePrScope: ["Expand tool allowlist", "Add sandboxed external tool execution"],
        notes: ["First safe tool execution boundary", "Deterministic internal tools only", "No side effects allowed"],
    },
    {
        id: "deterministic_internal_tool_allowlist",
        displayName: "Deterministic Internal Tool Allowlist",
        description: "Explicit allowlist of deterministic internal read-only tools. Only tools on this list may execute.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool not on allowlist", "Only deterministic internal tools permitted", "No dynamic tool loading"],
        futurePrScope: ["Allowlist expansion with approval", "Tool metadata registry"],
        notes: ["Allowlist is hardcoded and frozen", "No runtime allowlist modification", "Each tool must be deterministic and read-only"],
    },
    {
        id: "readonly_capability_lookup_tool",
        displayName: "Readonly Capability Lookup Tool",
        description: "Deterministic read-only tool that looks up registered capabilities from frozen registry.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["capability_lookup_result_card"],
        relatedConversationStatePreviewIds: ["readonly_capability_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_capability_lookup_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Capability not found in registry", "Registry is frozen — no mutation", "Lookup is read-only"],
        futurePrScope: ["Dynamic capability discovery", "Capability versioning"],
        notes: ["Reads from frozen registry only", "No side effects", "Deterministic output for same input"],
    },
    {
        id: "readonly_route_summary_tool",
        displayName: "Readonly Route Summary Tool",
        description: "Deterministic read-only tool that summarizes registered UI route metadata from frozen registry.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantUiRoutePreviewRegistry"],
        relatedRouteIds: ["route_summary_request"],
        relatedUiPreviewIds: ["route_summary_route_card"],
        relatedChatPreviewIds: ["route_summary_result_card"],
        relatedConversationStatePreviewIds: ["readonly_route_summary_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_route_summary_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Route not found in registry", "Registry is frozen — no mutation", "Summary is read-only"],
        futurePrScope: ["Dynamic route discovery", "Route access control"],
        notes: ["Reads from frozen route registry only", "No side effects", "Deterministic output for same input"],
    },
    {
        id: "readonly_safety_explain_tool",
        displayName: "Readonly Safety Explain Tool",
        description: "Deterministic read-only tool that explains safety flags and blocked actions from frozen registry.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantNoRuntimeSafetyAuditPackRegistry"],
        relatedRouteIds: ["safety_explain_request"],
        relatedUiPreviewIds: ["safety_explain_route_card"],
        relatedChatPreviewIds: ["safety_explain_result_card"],
        relatedConversationStatePreviewIds: ["readonly_safety_explain_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_safety_explain_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Safety flag not found", "Explanation is read-only", "No runtime modification of safety flags"],
        futurePrScope: ["Interactive safety explanation", "Safety flag history"],
        notes: ["Reads from frozen safety registry only", "No side effects", "Deterministic explanation for same query"],
    },
    {
        id: "tool_approval_required_gate",
        displayName: "Tool Approval Required Gate",
        description: "Gate requiring dual approval before any tool execution. No bypass allowed even for deterministic tools.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner approval missing", "User approval missing", "Dual approval required for all tool execution"],
        futurePrScope: ["Auto-approval for safe deterministic tools", "Approval delegation"],
        notes: ["Both owner and user must approve", "No approval bypass", "Gate enforced even for allowlisted tools"],
    },
    {
        id: "tool_result_redaction_contract",
        displayName: "Tool Result Redaction Contract",
        description: "Contract ensuring all tool execution results are redacted before display. No raw output exposed.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantGeneratedRedactionPolicyRegistry"],
        relatedRouteIds: ["audit_report_request"],
        relatedUiPreviewIds: ["audit_report_route_card"],
        relatedChatPreviewIds: ["redacted_result_preview_card"],
        relatedConversationStatePreviewIds: ["readonly_redacted_result_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_audit_evidence_placeholder"],
        relatedActionRequestPreviewIds: ["readonly_audit_action_request_placeholder"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Raw tool output not allowed", "All results must be redacted", "Redaction policy is mandatory"],
        futurePrScope: ["Configurable redaction rules", "Redaction audit log"],
        notes: ["No raw tool output reaches the user", "Redaction is applied before any display", "Redaction policy is frozen"],
    },
    {
        id: "tool_execution_audit_evidence",
        displayName: "Tool Execution Audit Evidence",
        description: "Audit evidence contract for all tool executions. Every execution produces immutable audit evidence.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["audit_report_request"],
        relatedUiPreviewIds: ["audit_report_route_card"],
        relatedChatPreviewIds: ["audit_transcript_preview"],
        relatedConversationStatePreviewIds: ["readonly_audit_transcript_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_audit_evidence_placeholder"],
        relatedActionRequestPreviewIds: ["readonly_audit_action_request_placeholder"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit evidence incomplete", "No execution without audit", "Audit is mandatory for all tool runs"],
        futurePrScope: ["Audit export", "Compliance reporting", "Audit retention policy"],
        notes: ["Every tool execution generates audit evidence", "Audit is append-only and immutable", "No audit deletion"],
    },
    {
        id: "forbidden_tool_runtime_boundary",
        displayName: "Forbidden Tool Runtime Boundary",
        description: "Hard boundary blocking all non-allowlisted tool execution. No shell, browser, desktop, network, provider, file mutation, or drive access.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["tool_execution_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool not on deterministic internal allowlist", "Shell/browser/desktop/network/provider/file-mutation/drive access blocked", "Only frozen registry read-only tools permitted"],
        futurePrScope: ["Sandboxed external tool execution", "Network-capable tool with approval"],
        notes: ["Hard boundary — no exceptions", "Blocked tools cannot be unlocked in PR99", "Boundary is enforced at registry level"],
    },
    {
        id: "future_smart_print_tool_ready",
        displayName: "Future Smart Print Tool Ready",
        description: "Placeholder for future Smart Print tool integration. Not active in PR99.",
        sourceModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["future_chat_runtime_blocked_card"],
        relatedConversationStatePreviewIds: ["future_persistent_conversation_state_blocked"],
        relatedApprovalStatePreviewIds: ["future_approval_runtime_blocked_state"],
        relatedActionRequestPreviewIds: ["future_action_execution_runtime_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Smart Print tool integration not yet available", "Requires PR100+ implementation", "All Smart Print drive access blocked"],
        futurePrScope: ["Smart Print read-only tool", "Smart Print data lookup", "Smart Print report generation"],
        notes: ["This entry is a future placeholder", "No Smart Print tool capability exists yet", "Activation requires separate PR"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantSafeInternalToolExecutionEntries() {
    return registry;
}
export function getSmartShikkaAssistantSafeInternalToolExecutionEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantSafeInternalToolExecutionRegistry.js.map